import {DialogBody, DialogFooter} from '@cypro/ui/components'
import {useGetGlobalSearchPatients} from '@/modules/patients/api'
import {getPatientFullName} from '@/modules/patients/functions'
import {SHORTCUTS} from '@/modules/shortcuts/constants'
import {focusNextRow, focusPrevRow, getRows} from '@/modules/shortcuts/functions'
import {useManualEntryBlocked} from '@/shared/hooks/use-manual-entry-flags'
import {EmptyState} from '@cypro/ui/components/ui/empty-state'
import {LinkButton} from '@cypro/ui/components/ui/link-button'
import {LoadingLogo} from '@cypro/ui/components/ui/loading-logo'
import {useDebouncedState} from '@cypro/ui/hooks/use-debounced-state'
import {useAppForm} from '@cypro/ui/integrations/form'
import {getAge, getShortDateFormatter, isDefined} from '@cypro/ui/utils'
import {Avatar, Badge, Box, ButtonGroup, Dialog, Heading, HStack, Text} from '@chakra-ui/react'
import {PatientSource} from '@cypro/engine/database/schema'
import {Patient} from '@cypro/engine/schema'
import {useStore} from '@tanstack/react-form'
import {useHotkeys} from '@tanstack/react-hotkeys'
import {Link} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {KeyboardEvent, PropsWithChildren} from 'react'
import {
  LuArrowDown,
  LuArrowUp,
  LuChevronRight,
  LuCornerDownLeft,
  LuSearch,
  LuUserRoundPlus,
  LuUserSearch,
} from 'react-icons/lu'
import {openPatientDrawer} from '@/modules/patients/components/patient-drawer/util'

const LIMIT = 25
const PATIENT_RESULT_ROLE = 'patient-global-search-result'

export function GLobalSearchPatientsDialog() {
  const {t} = useTranslate('common')
  const {manualPatientsBlocked} = useManualEntryBlocked()

  const form = useAppForm({
    defaultValues: {
      search: '',
    },
  })

  const search = useDebouncedState(
    useStore(form.store, state => state.values.search),
    300
  )

  const {patients, isFetching} = useGetGlobalSearchPatients({
    limit: LIMIT,
    filters: {
      name: search,
      source: [PatientSource.enum.manual, PatientSource.enum.hl7_import],
    },
  })

  useHotkeys([
    {
      hotkey: SHORTCUTS.NAVIGATE_ROW_DOWN,
      callback: e => {
        e.preventDefault()
        const rows = getRows(PATIENT_RESULT_ROLE)
        if (!rows.length) return
        focusNextRow(rows)
      },
      options: {ignoreInputs: false, conflictBehavior: 'allow', stopPropagation: true},
    },
    {
      hotkey: SHORTCUTS.NAVIGATE_ROW_UP,
      callback: e => {
        e.preventDefault()
        const rows = getRows(PATIENT_RESULT_ROLE)
        if (!rows.length) return
        focusPrevRow(rows)
      },
      options: {ignoreInputs: false, conflictBehavior: 'allow', stopPropagation: true},
    },
  ])

  return (
    <>
      <Dialog.Header p="0">
        <Dialog.Title srOnly>{t('global_search_dialog.title')}</Dialog.Title>
        <Box width="100%">
          <form onSubmit={e => e.preventDefault()}>
            <form.AppForm>
              <form.AppField name="search">
                {field => (
                  <field.Searchbox
                    maxWidth="100%"
                    roundedBottom={search ? '0' : undefined}
                    autoComplete="off"
                    autoCorrect="off"
                  />
                )}
              </form.AppField>
            </form.AppForm>
          </form>
        </Box>
      </Dialog.Header>

      {search && (
        <>
          <DialogBody
            p="0"
            minHeight="30rem"
            maxHeight="30rem"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            {!patients.length && isFetching && <LoadingLogo flex="1" />}

            {!patients.length && !isFetching && (
              <EmptyState
                maxWidth="sm"
                py="6"
                px="4"
                mx="auto"
                title={t('global_search_dialog.empty_state.title')}
                description={
                  <>
                    <span>{t('global_search_dialog.empty_state.description.1', {search})}</span>
                    <span>{t('global_search_dialog.empty_state.description.2')}</span>
                  </>
                }
                icon={LuUserSearch}
              >
                <ButtonGroup width="100%" justifyContent="center" gapX="4" size="sm">
                  <LinkButton to="/patient-dashboard/patients/new" colorPalette="cyan" disabled={manualPatientsBlocked}>
                    <LuUserRoundPlus />
                    {t('global_search_dialog.empty_state.add_action')}
                  </LinkButton>
                  <LinkButton
                    to="/patient-dashboard/patients"
                    search={{filters: {source: [PatientSource.enum.hl7_message], search: search}}}
                    state={{clearSearchOnTabChange: true}}
                    variant="outline"
                  >
                    <LuSearch />
                    {t('global_search_dialog.empty_state.hl7_action')}
                  </LinkButton>
                </ButtonGroup>
              </EmptyState>
            )}

            {patients.length > 0 && (
              <Box pt="4" display="flex" flexDirection="column" width="100%" height="100%" flex="1" overflow="hidden">
                <Box px="3" mb="1.5">
                  <Heading as="h3" size="sm" fontWeight="medium" fontFamily="body" color="fg.muted">
                    {t('results')}
                  </Heading>
                </Box>

                <Box spaceY="1" overflowY="auto" height="100%" flex="1">
                  {patients.map(patient => (
                    <PatientResult key={patient.id} {...patient} />
                  ))}
                </Box>
              </Box>
            )}
          </DialogBody>

          {patients.length > 0 && (
            <DialogFooter width="100%" display="flex" alignItems="center" justifyContent="space-between" p="3">
              <Box spaceX="4" display="flex" alignItems="center">
                <KeyBinding label={t('to_navigate').toLowerCase()}>
                  <LuArrowUp size={12} />
                  <LuArrowDown size={12} />
                </KeyBinding>

                <KeyBinding label={t('to_enter').toLowerCase()}>
                  <LuCornerDownLeft size={12} />
                </KeyBinding>
              </Box>

              <KeyBinding label={t('to_close').toLowerCase()}>esc</KeyBinding>
            </DialogFooter>
          )}
        </>
      )}
    </>
  )
}

function PatientResult(patient: Patient) {
  const {t} = useTranslate('common')
  const fullName = getPatientFullName(patient)
  const birthDate = isDefined(patient.birthDate) ? new Date(patient.birthDate) : null
  const characteristic = patient.characteristics?.at(-1)

  const details = []
  if (patient.sex) details.push(t(patient.sex))
  if (birthDate)
    details.push(`${getShortDateFormatter().format(birthDate)} (${t('x_yrs', {value: getAge(birthDate.getTime())})})`)
  if (characteristic?.diagnosis) details.push(characteristic.diagnosis.name)

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault()
      openPatientDrawer(patient)
    }
  }

  return (
    <Box
      asChild
      key={patient.id}
      py="1"
      px="3"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      spaceX="4"
      className="group"
      _hover={{bg: 'bg.muted'}}
      _focus={{bg: 'bg.subtle'}}
      data-role={PATIENT_RESULT_ROLE}
    >
      <Link
        to="/patient-dashboard/patients/$patientId"
        params={{patientId: patient.id}}
        search={{tab: 'therapy'}}
        onKeyDown={onKeyDown}
      >
        <HStack>
          <Avatar.Root size="sm" variant="subtle" shape="rounded">
            <Avatar.Fallback name={fullName} />
          </Avatar.Root>
          <Box>
            <Text fontSize="sm" fontWeight="medium">
              {fullName}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {details.join(' • ')}
            </Text>
          </Box>
        </HStack>

        <Box asChild opacity="0" _groupHover={{opacity: '1'}} _groupFocus={{opacity: '1'}}>
          <LuChevronRight size={20} />
        </Box>
      </Link>
    </Box>
  )
}

interface KeyBindingProps extends PropsWithChildren {
  label: string
}

function KeyBinding({children, label}: KeyBindingProps) {
  return (
    <Box display="flex" spaceX="2" alignItems="center">
      <Badge variant="subtle" size="sm" spaceX="2">
        {children}
      </Badge>

      <Text fontSize="sm" fontWeight="medium" color="fg.muted">
        {label}
      </Text>
    </Box>
  )
}
